import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import { GraphQLClient, gql } from 'graphql-request';

import type {
  GraphQLRecentTransaction,
  GraphQLSupporter,
  GraphQLSupporterContact,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isObject } from '@sola/shared-utils/misc.ts';

import { MOCK_SUPPORTER, QUERY_KEYS } from './constants.ts';
import { MOCK_SUPPORTER_GIFT_LIMITS } from './mocks/giftLimits.ts';

const graphqlEndpoint =
  process.env.GRAPHQL_PROXY_DESTINATION ||
  `${process.env.NEXT_PUBLIC_BASE_URL_PROXY_API}/graphql`;

/**
 * GraphQL Client
 * If needbe, we can configure error handling in the middleware.
 */
const client = new GraphQLClient(graphqlEndpoint, {
  credentials: 'include',
});

type GraphQLErrorResponse = {
  message: string;
};

export const isGraphQLResponseError = (
  response: unknown,
): response is GraphQLErrorResponse => {
  return (
    isObject(response) && 'message' in (response as Record<string, unknown>)
  );
};

type SelectedSupporterData<TKey> = TKey extends keyof GraphQLSupporter
  ? Pick<GraphQLSupporter, TKey>
  : Pick<GraphQLSupporter, keyof GraphQLSupporter>;

type SupporterField<TKey extends keyof GraphQLSupporter> = NonNullable<
  GraphQLSupporter[TKey]
>;

/**
 * Response from a graphql query.
 * It allows us to specify the data being returned, which is generally recommended.
 */
type SupporterResponse<TKey extends keyof GraphQLSupporter> =
  | {
      supporter: SelectedSupporterData<TKey>;
      // null is to handle errors, double check this.
    }
  | GraphQLErrorResponse;

type SupporterBundleResponse<TData> =
  | {
      supporter: TData;
    }
  | GraphQLErrorResponse;

type RecentLettersData = {
  lettersAvailable: NonNullable<SupporterField<'lettersAvailable'>>;
  lettersInTransit: NonNullable<SupporterField<'lettersInTransit'>>;
};

export type SupporterQueryContext = {
  queryClient: QueryClient;
};

export type SupporterQuery<TData> = Omit<UseQueryOptions<TData>, 'queryFn'> & {
  queryFn: (
    globalId: string,
    queryContext?: SupporterQueryContext,
  ) => Promise<TData>;
  waitForPrimer?: boolean;
};

export type InferSupporterQueryData<TQuery extends SupporterQuery<unknown>> =
  Awaited<ReturnType<TQuery['queryFn']>>;

export type SupporterDetailsData = Pick<
  GraphQLSupporter,
  | 'displayName'
  | 'firstName'
  | 'lastName'
  | 'preferredName'
  | 'salutation'
  | 'suffix'
  | 'organizationName'
  | 'email'
  | 'phone'
  | 'gender'
  | 'primaryLanguage'
  | 'correspondenceDeliveryPreference'
  | 'isGroup'
  | 'undeliverableEmail'
  | 'undeliverablePhone'
  | 'type'
  | 'typeLevel2'
  | 'typeLevel3'
  | 'status'
  | 'recordType'
  | 'sourceCode'
  | 'globalId'
>;

export type PrimedSupporterData = Pick<
  GraphQLSupporter,
  | 'dataStatus'
  | 'addressDetails'
  | 'commitments'
  | 'departureSummary'
  | 'givingTotals'
  | 'paymentMethods'
  | 'pledges'
> & {
  details: SupporterDetailsData | null;
};

export type SupporterQueryPart = keyof PrimedSupporterData;

type SupporterDocumentPart = {
  fragment: string;
  selection: string;
};

type SupporterBundlePartData = Partial<PrimedSupporterData>;

type SupporterBundleParts<TParts extends readonly SupporterQueryPart[]> = Pick<
  PrimedSupporterData,
  TParts[number]
>;

const defaultSupporterDataStatus: SupporterField<'dataStatus'> = {
  isPartial: false,
  issues: [],
};

const supporterDataStatusFragment = gql`
  fragment SupporterDataIssueFields on SupporterDataIssue {
    key
    message
    retryable
    stale
    lastAttemptAt
  }

  fragment SupporterDataStatusFields on SupporterDataStatus {
    isPartial
    issues {
      ...SupporterDataIssueFields
    }
  }
`;

const supporterAddressDetailsFragment = gql`
  fragment SupporterAddressDetailsFields on SupporterAddress {
    street
    street2
    city
    state
    postalCode
    country
    isUndeliverable
  }
`;

const supporterCommitmentsFragment = gql`
  fragment SupporterCommitmentsFields on Commitment {
    amountDue
    participant {
      birthdate
      country
      gender
      globalId
      localId
      name
      photoUrl
    }
    commitmentType
    canGiveGifts
    canMakePayments
    canWrite
    creditRemaining
    dates {
      start
      end
    }
    frequency
    fund {
      code
      description
      icon
    }
    globalId
    internalId
    interventionId
    isCorrespondent
    isRecurring
    lastCorrespondenceDate
    paymentDateNext
    paymentDatePending
    hasMissedPayment
    missedPaymentAmount
    hasDueThisMonth
    dueThisMonthAmount
    paymentFrequency
    paymentMethodId
    paymentPullDay
    rate
    receiptorId
    sourceCode
    sponsorshipChangeState
    status
    transactionId
  }
`;

const supporterDepartureSummaryFragment = gql`
  fragment DepartureChildFields on DepartureChild {
    commitmentInternalId
    displayName
    globalId
    imageUrl
  }

  fragment DepartureSummaryFields on DepartureSummary {
    departures {
      autoAcceptDate
      effectiveDate
      exitType
      departedChild {
        ...DepartureChildFields
      }
      sub {
        completed
        pendingCommitmentId
        shouldDisplay
        child {
          ...DepartureChildFields
        }
      }
    }
    # subsRemaining
  }
`;

const supporterGivingTotalsFragment = gql`
  fragment SupporterGivingTotalsFields on GivingTotals {
    amountDue
    annual
    monthly
    quarterly
    semiAnnual
    totalMissedPaymentAmount
    totalDueThisMonthAmount
  }
`;

const supporterLettersFragment = gql`
  fragment LetterBeneficiaryFields on LetterBeneficiary {
    globalId
    name
  }

  fragment LetterSupporterFields on LetterSupporter {
    globalId
    preferredName
  }

  fragment LetterFields on Letter {
    id
    sourceLetterId
    status
    direction
    createdDate
    readyDate
    isFinalLetter
    isReadBySupporter
    letterUrl
    numberOfPages
    pages {
      url
    }
    beneficiary {
      ...LetterBeneficiaryFields
    }
    supporter {
      ...LetterSupporterFields
    }
  }
`;

const supporterPaymentMethodsFragment = gql`
  fragment SupporterPaymentMethodsFields on SupporterPaymentMethod {
    id
    status
    accountNickname
    billingAddress {
      attention
      street
      city
      stateCode
      postalCode
      country
    }
    paymentType
    creditCard {
      cardholderName
      cardType
      digitalType
      expirationDate
      expiryStatus
      lastFour
      responseCode
      token
    }
    ach {
      accountName
      lastFourOfAccountNumber
      routingNumber
      token
    }
  }
`;

const supporterPledgesFragment = gql`
  fragment SupporterPledgesFields on Pledge {
    totalPledgeAmount
    totalGivenToDate
    status
    startDate
    pledgeName
    pledgeId
    outstandingAmount
    nextInstallmentDate
    nextInstallmentAmount
    lastInstallmentDate
    lastInstallmentAmount
    endDate
  }
`;

const supporterContactsFragment = gql`
  fragment SupporterContactsFields on SupporterContact {
    id
    firstName
    lastName
    preferredName
    email
    phone
    isPrimary
    canVolunteer
  }
`;

const supporterRecentTransactionsFragment = gql`
  fragment SupporterRecentTransactionsFields on RecentTransaction {
    status
    transactionId
    createdAt
    completedAt
    lastUpdatedAt
    original {
      cartId
      requestedAt
      items {
        commitmentId
        commitmentType
        currentRate
        fundCode
        fundIcon
        frequency
        isRecurring
        beneficiaryGlobalId
        beneficiaryPhotoUrl
      }
    }
    error {
      step
      message
    }
  }
`;

const supporterGiftLimitsFragment = gql`
  fragment SupporterGiftPaymentOptionsFields on SupporterGiftPaymentOptions {
    oneTime
    monthly
    quarterly
    semiAnnual
    annual
  }

  fragment SupporterGiftMinMaxAmountFields on SupporterGiftMinMaxAmount {
    minAmount
    maxAmount
  }

  fragment SupporterGiftPaymentOptionsMinMaxFields on SupporterGiftPaymentOptionsMinMax {
    oneTime {
      ...SupporterGiftMinMaxAmountFields
    }
    monthly {
      ...SupporterGiftMinMaxAmountFields
    }
    quarterly {
      ...SupporterGiftMinMaxAmountFields
    }
    semiAnnual {
      ...SupporterGiftMinMaxAmountFields
    }
    annual {
      ...SupporterGiftMinMaxAmountFields
    }
  }

  fragment SupporterGiftLimitItemFields on SupporterGiftLimitItem {
    remainingAmount
    remainingQuantity
    ineligibilityCode
    paymentOptions {
      ...SupporterGiftPaymentOptionsFields
    }
    paymentOptionsMinMax {
      ...SupporterGiftPaymentOptionsMinMaxFields
    }
  }

  fragment SupporterGiftTypeLimitsFields on SupporterGiftTypeLimits {
    Birthday {
      ...SupporterGiftLimitItemFields
    }
    Child {
      ...SupporterGiftLimitItemFields
    }
    Family {
      ...SupporterGiftLimitItemFields
    }
    Center {
      ...SupporterGiftLimitItemFields
    }
    Final {
      ...SupporterGiftLimitItemFields
    }
  }

  fragment SupporterGiftLimitsFields on SupporterGiftLimits {
    children {
      centerId
      id
      giftTypes {
        ...SupporterGiftTypeLimitsFields
      }
    }
    projects {
      id
      giftTypes {
        ...SupporterGiftTypeLimitsFields
      }
    }
    defaults {
      ...SupporterGiftTypeLimitsFields
    }
  }
`;

const supporterVolunteerEventsFragment = gql`
  fragment VolunteerVenueFields on VolunteerVenue {
    name
    address1
    address2
    city
    state
    postalCode
    country
  }

  fragment VolunteerSlotFields on VolunteerSlot {
    id
    startDate
    endDate
    status
    shiftName
    isWaiverComplete
    contactId
  }

  fragment SupporterVolunteerEventFields on VolunteerEvent {
    id
    locationName
    description
    startDate
    endDate
    venue {
      ...VolunteerVenueFields
    }
    slots {
      ...VolunteerSlotFields
    }
  }
`;

const supporterWritingPreferencesFragment = gql`
  fragment SupporterWritingPreferencesFields on Supporter {
    writingPreferences
  }
`;

const supporterDetailsFragment = gql`
  fragment SupporterDetailsFields on Supporter {
    displayName
    firstName
    lastName
    preferredName
    salutation
    suffix
    organizationName
    email
    phone
    gender
    globalId
    primaryLanguage
    correspondenceDeliveryPreference
    isGroup
    undeliverableEmail
    undeliverablePhone
    type
    typeLevel2
    typeLevel3
    status
    recordType
    sourceCode
  }
`;

const supporterDocumentParts = {
  dataStatus: {
    fragment: supporterDataStatusFragment,
    selection: `
      dataStatus {
        ...SupporterDataStatusFields
      }
    `,
  },
  addressDetails: {
    fragment: supporterAddressDetailsFragment,
    selection: `
      addressDetails {
        ...SupporterAddressDetailsFields
      }
    `,
  },
  commitments: {
    fragment: supporterCommitmentsFragment,
    selection: `
      commitments {
        ...SupporterCommitmentsFields
      }
    `,
  },
  departureSummary: {
    fragment: supporterDepartureSummaryFragment,
    selection: `
      departureSummary {
        ...DepartureSummaryFields
      }
    `,
  },
  givingTotals: {
    fragment: supporterGivingTotalsFragment,
    selection: `
      givingTotals {
        ...SupporterGivingTotalsFields
      }
    `,
  },
  paymentMethods: {
    fragment: supporterPaymentMethodsFragment,
    selection: `
      paymentMethods {
        ...SupporterPaymentMethodsFields
      }
    `,
  },
  pledges: {
    fragment: supporterPledgesFragment,
    selection: `
      pledges {
        ...SupporterPledgesFields
      }
    `,
  },
  details: {
    fragment: supporterDetailsFragment,
    selection: `
      ...SupporterDetailsFields
    `,
  },
} satisfies Record<SupporterQueryPart, SupporterDocumentPart>;

const supporterQueryPartKeys = {
  dataStatus: [QUERY_KEYS.supporterDataStatus],
  addressDetails: [QUERY_KEYS.supporterAddress],
  commitments: [QUERY_KEYS.supporterCommitments],
  departureSummary: [QUERY_KEYS.supporterDepartureSummary],
  givingTotals: [QUERY_KEYS.supporterGivingTotals],
  paymentMethods: [QUERY_KEYS.supporterPaymentMethods],
  pledges: [QUERY_KEYS.supporterPledges],
  details: [QUERY_KEYS.supporterDetails],
} as const satisfies Record<SupporterQueryPart, readonly [string]>;

const getSupporterQueryPartKey = (
  part: SupporterQueryPart,
  globalId?: string,
) => [...supporterQueryPartKeys[part], globalId] as const;

const buildSupporterQueryDocument = (
  operationName: string,
  parts: readonly SupporterQueryPart[],
) => {
  const fragments = [
    ...new Set(parts.map((part) => supporterDocumentParts[part].fragment)),
  ].join('\n');
  const selections = parts
    .map((part) => supporterDocumentParts[part].selection)
    .join('\n');

  return gql`
    ${fragments}
    query ${operationName}($globalId: String!) {
      supporter(globalId: $globalId) {
        ${selections}
      }
    }
  `;
};

const supporterBundleDocumentCache = new Map<string, string>();

const getSupporterBundleDocument = (
  operationName: string,
  parts: readonly SupporterQueryPart[],
) => {
  const cacheKey = `${operationName}:${parts.join('|')}`;
  const cachedDocument = supporterBundleDocumentCache.get(cacheKey);

  if (cachedDocument) {
    return cachedDocument;
  }

  const document = buildSupporterQueryDocument(operationName, parts);
  supporterBundleDocumentCache.set(cacheKey, document);

  return document;
};

const primedSupporterParts = [
  'dataStatus',
  'addressDetails',
  'commitments',
  'departureSummary',
  'givingTotals',
  'paymentMethods',
  'pledges',
  'details',
] as const satisfies readonly SupporterQueryPart[];

const supporterDetailsDocument = buildSupporterQueryDocument(
  'GetSupporterDetails',
  ['details'],
);

const supporterAddressDocument = buildSupporterQueryDocument(
  'GetSupporterAddress',
  ['addressDetails'],
);

const commitmentsDocument = buildSupporterQueryDocument(
  'GetSupporterCommitments',
  ['commitments'],
);

const departureSummaryDocument = buildSupporterQueryDocument(
  'GetSupporterDepartureSummary',
  ['departureSummary'],
);

const givingTotalsDocument = buildSupporterQueryDocument(
  'GetSupporterGivingTotals',
  ['givingTotals'],
);

const paymentMethodsDocument = buildSupporterQueryDocument(
  'GetSupporterPaymentMethods',
  ['paymentMethods'],
);

const volunteerEventsDocument = gql`
  ${supporterVolunteerEventsFragment}
  query GetSupporterVolunteerEvents($globalId: String!) {
    supporter(globalId: $globalId) {
      volunteerEvents {
        ...SupporterVolunteerEventFields
      }
    }
  }
`;

const pledgesDocument = buildSupporterQueryDocument('GetPledges', ['pledges']);

const contactsDocument = gql`
  ${supporterContactsFragment}
  query GetSupporterContacts($globalId: String!) {
    supporter(globalId: $globalId) {
      contacts {
        ...SupporterContactsFields
      }
    }
  }
`;

const contactPreferencesFragment = gql`
  fragment ContactPreferencesFields on ContactPreference {
    doNotCall
    doNotContact
    doNotEmail
    doNotMail
    doNotText
    undeliverableEmail
    undeliverablePhone
    emailInterestCategories
    mailInterestCategories
    phoneInterestCategories
    textInterestCategories
    subscriptionLists
    lastLoginDate
  }
`;

const contactVolunteerFragment = gql`
  fragment ContactVolunteerFields on ContactVolunteer {
    zipCode
    radius
  }
`;

const contactsWithPreferencesDocument = gql`
  ${supporterContactsFragment}
  ${contactPreferencesFragment}
  ${contactVolunteerFragment}
  query GetSupporterContactsWithPreferences($globalId: String!) {
    supporter(globalId: $globalId) {
      contacts {
        ...SupporterContactsFields
        preferences {
          ...ContactPreferencesFields
        }
        volunteer {
          ...ContactVolunteerFields
        }
      }
    }
  }
`;

const recentTransactionsDocument = gql`
  ${supporterRecentTransactionsFragment}
  query GetSupporterRecentTransactions($globalId: String!) {
    supporter(globalId: $globalId) {
      recentTransactions {
        ...SupporterRecentTransactionsFields
      }
    }
  }
`;

const recentGivingDocument = gql`
  query GetSupporterRecentGiving($globalId: String!) {
    supporter(globalId: $globalId) {
      recentGiving {
        amount
        beneficiaryPreferredName
        beneficiaryGlobalId
        commitmentFrequency
        commitmentId
        fundCode
        fundDescription
        fundIcon
        givingType
        interventionId
        isRefund
        isTaxDeductible
        paymentMethodId
        pledgeId
        quantity
        receiptorId
        receiptorName
        recordDate
        recordId
        stockGiftInKindDescription
        stockTickerSymbol
        taxYear
        transactionId
      }
    }
  }
`;

const giftLimitsDocument = gql`
  ${supporterGiftLimitsFragment}
  query GetSupporterGiftLimits($globalId: String!) {
    supporter(globalId: $globalId) {
      giftLimits {
        ...SupporterGiftLimitsFields
      }
    }
  }
`;

const recentLettersDocument = gql`
  ${supporterLettersFragment}
  query GetRecentLetters($globalId: String!) {
    supporter(globalId: $globalId) {
      lettersAvailable {
        ...LetterFields
      }
      lettersInTransit {
        ...LetterFields
      }
    }
  }
`;

const writingPreferencesDocument = gql`
  ${supporterWritingPreferencesFragment}
  query GetSupporterWritingPreferences($globalId: String!) {
    supporter(globalId: $globalId) {
      ...SupporterWritingPreferencesFields
    }
  }
`;

const hasCachedSupporterQueryPart = (
  queryClient: QueryClient,
  part: SupporterQueryPart,
  globalId?: string,
) => {
  const queryState = queryClient.getQueryState(
    getSupporterQueryPartKey(part, globalId),
  );
  return queryState !== undefined && queryState.data !== undefined;
};

const getCachedSupporterQueryPartData = <TPart extends SupporterQueryPart>(
  queryClient: QueryClient,
  part: TPart,
  globalId?: string,
) => {
  return queryClient.getQueryData<PrimedSupporterData[TPart]>(
    getSupporterQueryPartKey(part, globalId),
  );
};

const getCachedSupporterBundleData = (
  queryClient: QueryClient,
  parts: readonly SupporterQueryPart[],
  globalId?: string,
) => {
  return parts.reduce<SupporterBundlePartData>((cachedData, part) => {
    if (!hasCachedSupporterQueryPart(queryClient, part, globalId)) {
      return cachedData;
    }

    return {
      ...cachedData,
      [part]: getCachedSupporterQueryPartData(queryClient, part, globalId),
    };
  }, {});
};

const getMissingSupporterQueryParts = (
  queryClient: QueryClient,
  parts: readonly SupporterQueryPart[],
  globalId?: string,
) => {
  return parts.filter(
    (part) => !hasCachedSupporterQueryPart(queryClient, part, globalId),
  );
};

const getDefaultSupporterQueryPartData = <TPart extends SupporterQueryPart>(
  part: TPart,
): PrimedSupporterData[TPart] => {
  switch (part) {
    case 'dataStatus': {
      return defaultSupporterDataStatus as PrimedSupporterData[TPart];
    }
    default: {
      return null as PrimedSupporterData[TPart];
    }
  }
};

/**
 * Extract SupporterDetailsData from a flat supporter object.
 * The fragment spreads fields directly onto the supporter, so they aren't
 * nested under a 'details' key like other data slices.
 */
const extractSupporterDetails = (
  supporter: Record<string, unknown>,
): SupporterDetailsData => ({
  displayName:
    (supporter.displayName as SupporterDetailsData['displayName']) ?? null,
  firstName: (supporter.firstName as SupporterDetailsData['firstName']) ?? null,
  lastName: (supporter.lastName as SupporterDetailsData['lastName']) ?? null,
  preferredName:
    (supporter.preferredName as SupporterDetailsData['preferredName']) ?? null,
  salutation:
    (supporter.salutation as SupporterDetailsData['salutation']) ?? null,
  suffix: (supporter.suffix as SupporterDetailsData['suffix']) ?? null,
  globalId: (supporter.globalId as SupporterDetailsData['globalId']) ?? null,
  organizationName:
    (supporter.organizationName as SupporterDetailsData['organizationName']) ??
    null,
  email: (supporter.email as SupporterDetailsData['email']) ?? null,
  phone: (supporter.phone as SupporterDetailsData['phone']) ?? null,
  gender: (supporter.gender as SupporterDetailsData['gender']) ?? null,
  primaryLanguage: (supporter.primaryLanguage as string) ?? null,
  correspondenceDeliveryPreference:
    (supporter.correspondenceDeliveryPreference as string) ?? '',
  isGroup: (supporter.isGroup as SupporterDetailsData['isGroup']) ?? null,
  undeliverableEmail:
    (supporter.undeliverableEmail as SupporterDetailsData['undeliverableEmail']) ??
    null,
  undeliverablePhone:
    (supporter.undeliverablePhone as SupporterDetailsData['undeliverablePhone']) ??
    null,
  type: (supporter.type as SupporterDetailsData['type']) ?? null,
  typeLevel2:
    (supporter.typeLevel2 as SupporterDetailsData['typeLevel2']) ?? null,
  typeLevel3:
    (supporter.typeLevel3 as SupporterDetailsData['typeLevel3']) ?? null,
  status: (supporter.status as string) ?? '',
  recordType:
    (supporter.recordType as SupporterDetailsData['recordType']) ?? null,
  sourceCode:
    (supporter.sourceCode as SupporterDetailsData['sourceCode']) ?? null,
});

const pickSupporterBundleData = (
  supporterData: PrimedSupporterData | GraphQLSupporter,
  parts: readonly SupporterQueryPart[],
) => {
  return parts.reduce<SupporterBundlePartData>((bundleData, part) => {
    // details needs to be extracted from flat fields when the source
    // is a GraphQLSupporter-shaped object (e.g. MOCK_SUPPORTER).
    if (part === 'details' && !(part in supporterData)) {
      return {
        ...bundleData,
        [part]: extractSupporterDetails(
          supporterData as unknown as Record<string, unknown>,
        ),
      };
    }

    return {
      ...bundleData,
      [part]: (supporterData as PrimedSupporterData)[part],
    };
  }, {});
};

const normalizeSupporterBundleData = (
  response: SupporterBundleResponse<SupporterBundlePartData>,
  parts: readonly SupporterQueryPart[],
) => {
  const isResponseError = isGraphQLResponseError(response);

  if (isResponseError) {
    throw new Error(response.message);
  }

  return parts.reduce<SupporterBundlePartData>((bundleData, part) => {
    // details fields are spread flat on the supporter object
    // (via fragment spread), not nested under a 'details' key.
    if (part === 'details') {
      const supporter = response.supporter;
      return {
        ...bundleData,
        [part]: supporter
          ? extractSupporterDetails(
              supporter as unknown as Record<string, unknown>,
            )
          : getDefaultSupporterQueryPartData(part),
      };
    }

    const partData = response.supporter?.[part];

    return {
      ...bundleData,
      [part]: partData ?? getDefaultSupporterQueryPartData(part),
    };
  }, {});
};

const buildSupporterBundleResult = <
  const TParts extends readonly SupporterQueryPart[],
>(
  parts: TParts,
  supporterData: SupporterBundlePartData,
): SupporterBundleParts<TParts> => {
  return parts.reduce<SupporterBundleParts<TParts>>((bundleData, part) => {
    const partData = supporterData[part];

    return {
      ...bundleData,
      [part]: partData ?? getDefaultSupporterQueryPartData(part),
    };
  }, {} as SupporterBundleParts<TParts>);
};

const getSupporterBundleData = async (
  globalId: string,
  parts: readonly SupporterQueryPart[],
  operationName = 'GetSupporterBundleData',
): Promise<SupporterBundlePartData> => {
  if (parts.length === 0) {
    return {};
  }

  if (MOCK_SUPPORTER) {
    return pickSupporterBundleData(MOCK_SUPPORTER, parts);
  }

  const response: SupporterBundleResponse<SupporterBundlePartData> =
    await client.request(getSupporterBundleDocument(operationName, parts), {
      globalId,
    });

  return normalizeSupporterBundleData(response, parts);
};

const createSupporterBundleQuery = <
  const TParts extends readonly SupporterQueryPart[],
>(
  queryKey: string,
  operationName: string,
  parts: TParts,
): SupporterQuery<SupporterBundleParts<TParts>> => {
  return {
    queryKey: [queryKey],
    queryFn: async (globalId, queryContext) => {
      const cachedData = queryContext?.queryClient
        ? getCachedSupporterBundleData(
            queryContext.queryClient,
            parts,
            globalId,
          )
        : {};
      const missingParts = queryContext?.queryClient
        ? getMissingSupporterQueryParts(
            queryContext.queryClient,
            parts,
            globalId,
          )
        : [...parts];
      const fetchedData = await getSupporterBundleData(
        globalId,
        missingParts,
        operationName,
      );

      return buildSupporterBundleResult(parts, {
        ...cachedData,
        ...fetchedData,
      });
    },
  };
};

export const primeSupporterData = (
  queryClient: QueryClient,
  supporterData: Partial<PrimedSupporterData>,
  globalId?: string,
) => {
  const isUnavailable = (part: SupporterQueryPart): boolean =>
    supporterData.dataStatus?.issues.some(
      (issue) => issue.key === part && !issue.stale,
    ) ?? false;

  if ('dataStatus' in supporterData) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('dataStatus', globalId),
      supporterData.dataStatus ?? defaultSupporterDataStatus,
    );
  }

  if (
    !isUnavailable('commitments') &&
    supporterData.commitments !== null &&
    supporterData.commitments !== undefined
  ) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('commitments', globalId),
      supporterData.commitments,
    );
  }

  if (
    !isUnavailable('departureSummary') &&
    supporterData.departureSummary !== null &&
    supporterData.departureSummary !== undefined
  ) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('departureSummary', globalId),
      supporterData.departureSummary,
    );
  }

  if (
    !isUnavailable('addressDetails') &&
    supporterData.addressDetails !== undefined
  ) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('addressDetails', globalId),
      supporterData.addressDetails ?? null,
    );
  }

  if (
    !isUnavailable('paymentMethods') &&
    supporterData.paymentMethods !== null &&
    supporterData.paymentMethods !== undefined
  ) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('paymentMethods', globalId),
      supporterData.paymentMethods,
    );
  }

  if (
    !isUnavailable('givingTotals') &&
    supporterData.givingTotals !== undefined
  ) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('givingTotals', globalId),
      supporterData.givingTotals,
    );
  }

  if (
    !isUnavailable('pledges') &&
    supporterData.pledges !== null &&
    supporterData.pledges !== undefined
  ) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('pledges', globalId),
      supporterData.pledges,
    );
  }

  if ('details' in supporterData && !isUnavailable('details')) {
    queryClient.setQueryData(
      getSupporterQueryPartKey('details', globalId),
      supporterData.details ?? null,
    );
  }
};

export const primeSupporterQuery = createSupporterBundleQuery(
  QUERY_KEYS.dataPrimerReadOnly,
  'GetPrimedSupporterData',
  primedSupporterParts,
);

/**
 * Get all commitments.
 *
 * // TODO possibly reduce to what we know we actually need.
 */
export const getCommitments = async (
  globalId: string,
): Promise<SupporterField<'commitments'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.commitments ?? [];
  }

  const response: SupporterResponse<'commitments'> = await client.request(
    commitmentsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response?.supporter?.commitments) {
    throw new Error('Empty commitment response.');
  }

  return response.supporter.commitments;
};

/**
 * Query all commitment data
 */
export const commitmentsQuery: SupporterQuery<SupporterField<'commitments'>> = {
  queryKey: [QUERY_KEYS.supporterCommitments],
  queryFn: (globalId) => getCommitments(globalId),
  waitForPrimer: true,
};

export const getDepartureSummary = async (
  globalId: string,
): Promise<SupporterField<'departureSummary'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.departureSummary;
  }

  const response: SupporterResponse<'departureSummary'> = await client.request(
    departureSummaryDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.departureSummary) {
    throw new Error('Empty departure summary response.');
  }

  return response.supporter.departureSummary;
};

export const departureSummaryQuery: SupporterQuery<
  SupporterField<'departureSummary'>
> = {
  queryKey: [QUERY_KEYS.supporterDepartureSummary],
  queryFn: (globalId) => getDepartureSummary(globalId),
  waitForPrimer: true,
};

export const getGivingTotals = async (
  globalId: string,
): Promise<SupporterField<'givingTotals'> | null> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.givingTotals ?? null;
  }

  const response: SupporterResponse<'givingTotals'> = await client.request(
    givingTotalsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.givingTotals) {
    throw new Error('Empty giving summary response.');
  }

  return response.supporter?.givingTotals;
};

/**
 * Query giving totals
 */
export const givingTotalsQuery: SupporterQuery<SupporterField<'givingTotals'> | null> =
  {
    queryKey: [QUERY_KEYS.supporterGivingTotals],
    queryFn: async (globalId: string) => getGivingTotals(globalId),
    waitForPrimer: true,
  };

/**
 * Get all payment method information.
 */
export const getPaymentMethods = async (
  globalId: string,
): Promise<SupporterField<'paymentMethods'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.paymentMethods ?? [];
  }

  const response = await client.request<SupporterResponse<'paymentMethods'>>(
    paymentMethodsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.paymentMethods) {
    throw new Error('Empty payment method response.');
  }

  return response.supporter.paymentMethods;
};

/**
 * Query all payment methods.
 * // TODO, clean up TS once we get things going.
 */
export const paymentMethodsQuery: SupporterQuery<
  SupporterField<'paymentMethods'>
> = {
  queryKey: [QUERY_KEYS.supporterPaymentMethods],
  queryFn: (globalId) => getPaymentMethods(globalId),
  waitForPrimer: true,
};

/**
 * Get the supporter's volunteer events with their slots and contacts.
 */
export const getVolunteerEvents = async (
  globalId: string,
): Promise<SupporterField<'volunteerEvents'>> => {
  if (MOCK_SUPPORTER) {
    return (
      (MOCK_SUPPORTER as unknown as GraphQLSupporter).volunteerEvents ?? []
    );
  }

  const response = await client.request<SupporterResponse<'volunteerEvents'>>(
    volunteerEventsDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.volunteerEvents) {
    throw new Error('Empty volunteer events response.');
  }

  return response.supporter.volunteerEvents;
};

/**
 * Query all volunteer events.
 */
export const volunteerEventsQuery: SupporterQuery<
  SupporterField<'volunteerEvents'>
> = {
  queryKey: [QUERY_KEYS.supporterVolunteerEvents],
  queryFn: (globalId) => getVolunteerEvents(globalId),
  waitForPrimer: true,
};

/**
 * Get suppoerter address
 */
export const getSupporterAddress = async (
  globalId: string,
): Promise<SupporterField<'addressDetails'> | null> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.addressDetails ?? null;
  }

  const response: SupporterResponse<'addressDetails'> = await client.request(
    supporterAddressDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  }

  return response.supporter.addressDetails ?? null;
};

export const supporterAddressQuery: SupporterQuery<SupporterField<'addressDetails'> | null> =
  {
    queryKey: [QUERY_KEYS.supporterAddress],
    queryFn: (globalId) => getSupporterAddress(globalId),
    waitForPrimer: true,
  };

export const getPledges = async (
  globalId: string,
): Promise<SupporterField<'pledges'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.pledges ?? [];
  }

  const response: SupporterResponse<'pledges'> = await client.request(
    pledgesDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.pledges) {
    throw new Error('Empty pledges response.');
  }

  return response.supporter.pledges;
};

export const pledgesQuery: SupporterQuery<SupporterField<'pledges'>> = {
  queryKey: [QUERY_KEYS.supporterPledges],
  queryFn: async (globalId: string) => getPledges(globalId),
  waitForPrimer: true,
};

export const getSupporterDetails = async (
  globalId: string,
): Promise<SupporterDetailsData | null> => {
  if (MOCK_SUPPORTER) {
    return extractSupporterDetails(
      MOCK_SUPPORTER as unknown as Record<string, unknown>,
    );
  }

  const response: SupporterBundleResponse<SupporterDetailsData> =
    await client.request(supporterDetailsDocument, { globalId });

  if (isGraphQLResponseError(response)) {
    return null;
  }
  return response.supporter
    ? extractSupporterDetails(
        response.supporter as unknown as Record<string, unknown>,
      )
    : null;
};

export const supporterDetailsQuery: SupporterQuery<SupporterDetailsData | null> =
  {
    queryKey: [QUERY_KEYS.supporterDetails],
    queryFn: (globalId) => getSupporterDetails(globalId),
    waitForPrimer: true,
  };

const preferredUsernameDocument = gql`
  query GetPreferredUsername($globalId: String!) {
    supporter(globalId: $globalId) {
      preferredUsername
    }
  }
`;

export const getPreferredUsername = async (
  globalId: string,
): Promise<string | null> => {
  if (MOCK_SUPPORTER) {
    return (
      (MOCK_SUPPORTER as unknown as GraphQLSupporter).preferredUsername ?? null
    );
  }

  try {
    const response: SupporterResponse<'preferredUsername'> =
      await client.request(preferredUsernameDocument, { globalId });

    if (isGraphQLResponseError(response)) {
      return null;
    }

    return response.supporter?.preferredUsername ?? null;
  } catch {
    // Query may fail if the deployed AppSync schema doesn't have preferredUsername yet
    return null;
  }
};

export const preferredUsernameQuery: SupporterQuery<string | null> = {
  queryKey: [QUERY_KEYS.supporterPreferredUsername],
  queryFn: (globalId) => getPreferredUsername(globalId),
};

export const getSupporterContacts = async (
  globalId: string,
): Promise<GraphQLSupporterContact[] | null> => {
  if (MOCK_SUPPORTER) {
    return (MOCK_SUPPORTER as unknown as GraphQLSupporter).contacts ?? null;
  }

  try {
    const response: SupporterResponse<'contacts'> = await client.request(
      contactsDocument,
      { globalId },
    );

    if (isGraphQLResponseError(response)) {
      return null;
    }

    return response.supporter?.contacts ?? null;
  } catch {
    return null;
  }
};

export const supporterContactsQuery: SupporterQuery<
  GraphQLSupporterContact[] | null
> = {
  queryKey: [QUERY_KEYS.supporterContacts],
  queryFn: (globalId) => getSupporterContacts(globalId),
  waitForPrimer: true,
};

/**
 * Fetches contacts with preferences and volunteer data.
 * This is a separate query from the basic contacts query so the UI
 * has something to show even if preferences are not yet available.
 */
export const getSupporterContactsWithPreferences = async (
  globalId: string,
): Promise<GraphQLSupporterContact[] | null> => {
  if (MOCK_SUPPORTER) {
    return (MOCK_SUPPORTER as unknown as GraphQLSupporter).contacts ?? null;
  }

  try {
    const response: SupporterResponse<'contacts'> = await client.request(
      contactsWithPreferencesDocument,
      { globalId },
    );

    if (isGraphQLResponseError(response)) {
      return null;
    }

    return response.supporter?.contacts ?? null;
  } catch {
    return null;
  }
};

export const supporterContactPreferencesQuery: SupporterQuery<
  GraphQLSupporterContact[] | null
> = {
  queryKey: [QUERY_KEYS.supporterContactPreferences],
  queryFn: (globalId) => getSupporterContactsWithPreferences(globalId),
  waitForPrimer: true,
};

export const getRecentTransactions = async (
  globalId: string,
): Promise<GraphQLRecentTransaction[]> => {
  if (MOCK_SUPPORTER) {
    return (
      (MOCK_SUPPORTER as unknown as GraphQLSupporter).recentTransactions ?? []
    );
  }

  const response: SupporterResponse<'recentTransactions'> =
    await client.request(recentTransactionsDocument, { globalId });

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  }

  return response.supporter?.recentTransactions ?? [];
};

export const recentTransactionsQuery: SupporterQuery<
  GraphQLRecentTransaction[]
> = {
  queryKey: [QUERY_KEYS.supporterRecentTransactions],
  queryFn: async (globalId) => getRecentTransactions(globalId),
  waitForPrimer: true,
};

export const getRecentGiving = async (
  globalId: string,
): Promise<SupporterField<'recentGiving'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER.recentGiving ?? [];
  }

  const response: SupporterResponse<'recentGiving'> = await client.request(
    recentGivingDocument,
    { globalId },
  );

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  } else if (!response.supporter?.recentGiving) {
    throw new Error('Empty recent giving response.');
  }

  return response.supporter.recentGiving;
};

export const recentGivingQuery: SupporterQuery<SupporterField<'recentGiving'>> =
  {
    queryKey: [QUERY_KEYS.supporterRecentGiving],
    queryFn: (globalId) => getRecentGiving(globalId),
    waitForPrimer: true,
  };

export const getGiftLimits = async (
  globalId: string,
): Promise<SupporterField<'giftLimits'>> => {
  if (MOCK_SUPPORTER) {
    return MOCK_SUPPORTER_GIFT_LIMITS;
  }

  const response: SupporterBundleResponse<{
    giftLimits: SupporterField<'giftLimits'> | null;
  }> = await client.request(giftLimitsDocument, { globalId });

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  }

  if (!response.supporter?.giftLimits) {
    throw new Error('Empty gift limits response.');
  }

  return response.supporter.giftLimits;
};

export const giftLimitsQuery: SupporterQuery<SupporterField<'giftLimits'>> = {
  queryKey: [QUERY_KEYS.supporterGiftLimits],
  queryFn: (globalId) => getGiftLimits(globalId),
  waitForPrimer: true,
};

export const getRecentLetters = async (
  globalId: string,
): Promise<RecentLettersData> => {
  if (MOCK_SUPPORTER) {
    return {
      lettersAvailable: MOCK_SUPPORTER.lettersAvailable ?? [],
      lettersInTransit: MOCK_SUPPORTER.lettersInTransit ?? [],
    };
  }

  const response: SupporterBundleResponse<{
    lettersAvailable: SupporterField<'lettersAvailable'>;
    lettersInTransit: SupporterField<'lettersInTransit'>;
  }> = await client.request(recentLettersDocument, { globalId });

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  }

  return {
    lettersAvailable: response.supporter?.lettersAvailable ?? [],
    lettersInTransit: response.supporter?.lettersInTransit ?? [],
  };
};

export const recentLettersQuery: SupporterQuery<RecentLettersData> = {
  queryKey: [QUERY_KEYS.supporterRecentLetters],
  queryFn: (globalId) => getRecentLetters(globalId),
  waitForPrimer: true,
};

export const getWritingPreferences = async (
  globalId: string,
): Promise<SupporterField<'writingPreferences'>> => {
  if (MOCK_SUPPORTER) {
    return (
      (MOCK_SUPPORTER as unknown as GraphQLSupporter).writingPreferences ?? []
    );
  }

  const response: SupporterResponse<'writingPreferences'> =
    await client.request(writingPreferencesDocument, { globalId });

  if (isGraphQLResponseError(response)) {
    throw new Error(response.message);
  }

  return response.supporter?.writingPreferences ?? [];
};

export const writingPreferencesQuery: SupporterQuery<
  SupporterField<'writingPreferences'>
> = {
  queryKey: [QUERY_KEYS.supporterWritingPreferences],
  queryFn: (globalId) => getWritingPreferences(globalId),
  waitForPrimer: true,
};
